/* ORLO's progressive variant/quantity display. Cart submit and Magic stay theme-owned. */
(() => {
  'use strict';
  if (window.mifoaOrlo) { window.mifoaOrlo.mount(document); return; }
  const instances = new Map();
  const mountOne = (root) => {
    if (instances.has(root)) return;
    let model;
    try { model = JSON.parse(root.querySelector('[data-orlo-model]')?.textContent || '{}'); }
    catch (_error) { return; }
    const variants = Array.isArray(model.variants) ? model.variants : [];
    const form = root.querySelector('[data-orlo-form]');
    const quantityInput = form?.querySelector('[data-orlo-quantity]');
    const button = form?.querySelector('[data-add-to-cart]');
    if (!form || !quantityInput || !button || !variants.length) return;
    const commerceReady = model.commerceReady === true && root.dataset.orloCommerceReady === 'true';
    const radios = [...form.querySelectorAll('[data-orlo-variant-input]')];
    const sticky = root.querySelector('[data-orlo-sticky]');
    const stickyButton = sticky?.querySelector('[data-orlo-sticky-submit]');
    const stickyPrice = sticky?.querySelector('[data-orlo-sticky-price]');
    const stickyQuantity = sticky?.querySelector('[data-orlo-sticky-quantity]');
    const stickyThumb = sticky?.querySelector('[data-orlo-sticky-thumb]');
    const stickyPlaceholder = sticky?.querySelector('[data-orlo-sticky-placeholder]');
    const priceRow = root.querySelector('[data-orlo-price-row]');
    const unitPrice = root.querySelector('[data-orlo-price]');
    const availability = form.querySelector('[data-orlo-availability]');
    const savings = form.querySelector('[data-orlo-savings]');
    const savingsText = form.querySelector('[data-orlo-savings-text]');
    const ladder = form.querySelector('[data-orlo-volume-ladder]');
    const anchor = root.querySelector('[data-orlo-buy-anchor]');
    const gallery = root.querySelector('[data-orlo-gallery]');
    const thumbs = [...(gallery?.querySelectorAll('[data-gallery-thumb]') || [])];
    const slides = [...(gallery?.querySelectorAll('[data-gallery-main]') || [])];
    const counter = gallery?.querySelector('[data-gallery-count]');
    const thumbRail = gallery?.querySelector('.mf-orlo__thumbnails');
    const mainSteps = [...form.querySelectorAll('[data-orlo-step]')];
    const stickySteps = [...(sticky?.querySelectorAll('[data-orlo-step]') || [])];
    const cleanups = [];
    const listen = (node, name, handler, options) => {
      node?.addEventListener(name, handler, options);
      cleanups.push(() => node?.removeEventListener(name, handler, options));
    };
    const findVariant = (id) => variants.find((variant) => String(variant.id) === String(id));
    // The server only enables this model after exact-product Admin verification.
    // These checks prevent stale display data when a product or price is changed.
    const offer = model.offer || {};
    const offerReady = commerceReady && offer.enabled === true
      && String(model.productId) === '10325208695097'
      && root.dataset.orloProductId === String(model.productId)
      && model.currency === 'INR' && Number(offer.unitPrice) === 99900
      && Number(offer.off2) === 15000 && Number(offer.off3) === 25000
      && variants.every((variant) => Number(variant.price) === Number(offer.unitPrice));
    let selected = findVariant(model.selectedVariantId);
    if (!selected?.available) selected = variants.find((variant) => variant.available) || variants[0];
    let quantity = Number(quantityInput.value) || 1;
    let galleryIndex = 0;
    let busy = false;
    let expectedDisabled = button.disabled;
    let disposed = false;
    const rule = () => ({
      min: Math.max(1, Math.floor(Number(selected.min)) || 1),
      increment: Math.max(1, Math.floor(Number(selected.increment)) || 1),
      max: Number.isFinite(Number(selected.max)) && Number(selected.max) > 0 ? Math.floor(Number(selected.max)) : null,
    });
    const clamp = (value) => {
      const limits = rule();
      const number = Number.isFinite(Number(value)) ? Math.floor(Number(value)) : limits.min;
      let next = limits.min + Math.round(Math.max(0, number - limits.min) / limits.increment) * limits.increment;
      if (limits.max !== null) {
        const maximum = limits.min + Math.floor(Math.max(0, limits.max - limits.min) / limits.increment) * limits.increment;
        next = Math.min(next, maximum);
      }
      return Math.max(limits.min, next);
    };
    const money = (cents) => {
      const digits = cents % 100 === 0 ? 0 : 2;
      try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: model.currency || 'INR',
        currencyDisplay: 'narrowSymbol', minimumFractionDigits: digits, maximumFractionDigits: digits })
        .format(cents / 100).replace(/\u00a0/g, ' '); }
      catch (_error) { return '₹' + (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: digits }); }
    };
    const writeText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
    const writeAttribute = (node, name, value) => {
      if (!node) return;
      if (value === null) { if (node.hasAttribute(name)) node.removeAttribute(name); }
      else if (node.getAttribute(name) !== String(value)) node.setAttribute(name, String(value));
    };
    const showGallery = (index) => {
      if (!thumbs.length) return;
      galleryIndex = ((index % thumbs.length) + thumbs.length) % thumbs.length;
      const chosen = thumbs[galleryIndex];
      const key = chosen.dataset.galleryThumb;
      thumbs.forEach((thumb) => {
        const active = thumb === chosen;
        writeAttribute(thumb, 'aria-current', active ? 'true' : 'false');
        thumb.style.borderColor = active ? '#3B49BE' : 'transparent';
      });
      slides.forEach((slide) => {
        const active = slide.dataset.galleryMain === key;
        slide.style.opacity = active ? '1' : '0';
        slide.style.pointerEvents = active ? 'auto' : 'none';
        writeAttribute(slide, 'aria-hidden', active ? 'false' : 'true');
      });
      writeText(counter, `${galleryIndex + 1} / ${thumbs.length}`);
      // Move only the thumbnail rail, never the page or the focused finish input.
      if (thumbRail?.scrollTo) {
        const railRect = thumbRail.getBoundingClientRect();
        const thumbRect = chosen.getBoundingClientRect();
        const delta = thumbRect.left < railRect.left ? thumbRect.left - railRect.left
          : thumbRect.right > railRect.right ? thumbRect.right - railRect.right : 0;
        if (delta) thumbRail.scrollTo({ left: thumbRail.scrollLeft + delta,
          behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    };
    const imageIdentity = (value) => {
      if (!value) return '';
      try {
        // Variant ProductImage IDs and product MediaImage IDs are different.
        // A Shopify CDN path stays the same across responsive width parameters.
        const url = new URL(value, window.location?.href || 'https://mifoa.com');
        return `${url.host}${url.pathname}`.replace(/_\d+x\d*(?=\.[^.]+$)/, '');
      } catch (_error) { return String(value).split('?')[0]; }
    };
    const showFinishImage = () => {
      if (!thumbs.length) return;
      const selectedFinish = String(selected.finish || selected.title || '').trim().toLowerCase();
      const selectedImage = imageIdentity(selected.imageUrl);
      const match = thumbs.find((thumb) => selected.imageId && thumb.dataset.orloMediaId === String(selected.imageId))
        || thumbs.find((thumb) => selectedImage && imageIdentity(thumb.dataset.orloImageUrl) === selectedImage)
        || thumbs.find((thumb) => String(thumb.dataset.orloFinish || '').trim().toLowerCase() === selectedFinish)
        || null;
      // Clicking the native thumbnail also keeps the shared swipe controller's
      // index in step. No new swipe/drag implementation or image recolouring.
      match?.click();
    };
    const syncPurchase = () => {
      if (disposed) return;
      // Respect the native AJAX controller's temporary lock and Added state.
      // Only observe its button state; do not intercept submit or issue requests.
      if (button.disabled !== expectedDisabled) busy = button.disabled;
      quantity = clamp(quantity);
      const limits = rule();
      const hasPrice = commerceReady && Number.isFinite(Number(selected.price)) && Number(selected.price) > 0;
      const canBuy = hasPrice && selected.available === true;
      const label = !hasPrice ? 'Coming soon' : !selected.available ? 'Sold out' : 'Add to cart';
      const offEach = offerReady && quantity >= 3 ? Number(offer.off3)
        : offerReady && quantity >= 2 ? Number(offer.off2) : 0;
      const saved = hasPrice ? offEach * quantity : 0;
      const total = hasPrice ? money((Number(selected.price) - offEach) * quantity) : '';
      if (quantityInput.value !== String(quantity)) quantityInput.value = String(quantity);
      writeAttribute(quantityInput, 'value', quantity);
      writeAttribute(quantityInput, 'min', limits.min);
      writeAttribute(quantityInput, 'step', limits.increment);
      writeAttribute(quantityInput, 'max', limits.max);
      radios.forEach((radio) => {
        const variant = findVariant(radio.value);
        radio.checked = String(radio.value) === String(selected.id);
        radio.disabled = !variant?.available;
      });
      if (priceRow) priceRow.hidden = !hasPrice;
      writeText(unitPrice, hasPrice ? money(Number(selected.price)) : '');
      writeText(availability, label === 'Add to cart' ? '' : label);
      if (ladder) ladder.hidden = !offerReady;
      if (savings) savings.hidden = saved <= 0;
      writeText(savingsText, saved > 0 ? `You save ${money(saved)} · ${money(offEach)} off each ORLO` : '');
      writeText(stickyPrice, total);
      writeText(stickyQuantity, String(quantity));
      const finishName = selected.finish || selected.title;
      document.querySelectorAll('[data-orlo-finish-label]').forEach((node) => writeText(node, finishName));
      [...mainSteps, ...stickySteps].forEach((step) => {
        const next = quantity + Number(step.dataset.orloStep) * limits.increment;
        step.disabled = next < limits.min || (limits.max !== null && next > limits.max);
      });
      expectedDisabled = busy || !canBuy;
      if (button.disabled !== expectedDisabled) button.disabled = expectedDisabled;
      if (stickyButton && stickyButton.disabled !== expectedDisabled) stickyButton.disabled = expectedDisabled;
      if (!busy) {
        if (canBuy) {
          let price = button.querySelector('[data-mf-add-price]');
          if (!price) {
            button.innerHTML = '<span>Add to cart · <span data-mf-add-price></span></span>';
            price = button.querySelector('[data-mf-add-price]');
          }
          writeText(price, total);
        } else writeText(button, label);
        writeText(stickyButton, label);
      }
    };
    const announceVariant = () => {
      document.dispatchEvent(new CustomEvent('orlo:variant-change', {
        detail: { variant: selected, finishName: selected.finish || selected.title },
      }));
    };
    const selectVariant = (id) => {
      const next = findVariant(id);
      if (!next) return false;
      selected = next;
      syncPurchase();
      showFinishImage();
      if (stickyThumb) {
        if (selected.imageUrl) {
          writeAttribute(stickyThumb, 'src', selected.imageUrl);
          stickyThumb.hidden = false;
          if (stickyPlaceholder) stickyPlaceholder.hidden = true;
        } else {
          const displayed = thumbs[galleryIndex]?.querySelector('img') || slides[galleryIndex]?.querySelector('img');
          if (displayed?.currentSrc || displayed?.src) {
            writeAttribute(stickyThumb, 'src', displayed.currentSrc || displayed.src);
            stickyThumb.hidden = false;
            if (stickyPlaceholder) stickyPlaceholder.hidden = true;
          } else {
            stickyThumb.hidden = true;
            if (stickyPlaceholder) stickyPlaceholder.hidden = false;
          }
        }
      }
      announceVariant();
      return true;
    };
    const setQuantity = (value) => { quantity = clamp(value); syncPurchase(); };
    listen(form, 'change', (event) => {
      if (event.target.matches('[data-orlo-variant-input]')) selectVariant(event.target.value);
      else if (event.target === quantityInput) setQuantity(quantityInput.value);
    });
    const onStep = (event) => {
      const step = event.target.closest('[data-orlo-step]');
      if (!step || step.disabled) return;
      setQuantity(quantity + Number(step.dataset.orloStep) * rule().increment);
    };
    listen(form, 'click', onStep);
    listen(sticky, 'click', onStep);
    listen(gallery, 'click', (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb && thumbs.includes(thumb)) showGallery(thumbs.indexOf(thumb));
    });
    listen(gallery, 'keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!event.target.closest('[data-gallery-thumb]')) return;
      event.preventDefault();
      const next = (galleryIndex + (event.key === 'ArrowRight' ? 1 : -1) + thumbs.length) % thumbs.length;
      thumbs[next]?.click();
      thumbs[next]?.focus();
    });
    const observer = new MutationObserver(syncPurchase);
    observer.observe(quantityInput, { attributes: true, attributeFilter: ['value'] });
    observer.observe(button, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true });
    if (stickyButton) observer.observe(stickyButton, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true });
    cleanups.push(() => observer.disconnect());
    let scrollFrame = null;
    const updateSticky = () => {
      scrollFrame = null;
      if (!sticky || disposed) return;
      const rect = anchor?.getBoundingClientRect();
      const visible = Boolean(rect && rect.height > 0 && rect.bottom < 0);
      sticky.classList.toggle('is-on', visible);
      writeAttribute(sticky, 'aria-hidden', visible ? 'false' : 'true');
      sticky.inert = !visible;
      const footer = document.querySelector('.mf-footer') || document.querySelector('footer');
      const overlap = footer ? Math.max(0, window.innerHeight - footer.getBoundingClientRect().top) : 0;
      sticky.style.bottom = overlap ? `${overlap}px` : '';
    };
    const requestSticky = () => {
      if (scrollFrame === null) scrollFrame = window.requestAnimationFrame(updateSticky);
    };
    if (sticky) {
      document.body.appendChild(sticky);
      listen(window, 'scroll', requestSticky, { passive: true });
      listen(window, 'resize', requestSticky, { passive: true });
    }
    const dispose = () => {
      if (disposed) return;
      disposed = true;
      cleanups.forEach((cleanup) => cleanup());
      if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
      sticky?.remove();
      instances.delete(root);
    };
    instances.set(root, { selectVariant, dispose, getSelected: () => selected });
    selectVariant(selected.id);
    updateSticky();
    window.__mfGallerySwipeInit?.();
  };
  const mount = (scope) => {
    [...instances.entries()].forEach(([root, instance]) => { if (!root.isConnected) instance.dispose(); });
    if (scope.matches?.('[data-orlo]')) mountOne(scope);
    scope.querySelectorAll('[data-orlo]').forEach(mountOne);
  };
  window.mifoaOrlo = { mount, selectVariant: (id) => {
    let changed = false;
    instances.forEach((instance) => { changed = instance.selectVariant(id) || changed; });
    return changed;
  } };
  document.addEventListener('click', (event) => {
    const target = event.target.closest?.('[data-orlo-select-variant]');
    if (!target || target.disabled) return;
    window.mifoaOrlo.selectVariant(target.dataset.variantId);
  });
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    [...instances.entries()].forEach(([root, instance]) => {
      if (event.target === root || event.target.contains(root)) instance.dispose();
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
})();
